import { useLayoutEffect, useRef, useState } from 'react';
import { PleinEcran } from '../../components/ui/PleinEcran.jsx';
import { DEVIS_STYLES } from '../../constants/documents';
import { tr } from '../../lib/i18n';
import { autoLogoColor } from '../../lib/logo';
import { C, DA } from '../../theme/palette';
import { DocumentA4 } from '../documents/DocumentA4.jsx';
import { COULEURS_DOCUMENT, offreDExemple } from './offre-d-exemple';

/** Arc-en-ciel du rond « Autre » : toutes les couleurs. */
const ARC_EN_CIEL = 'conic-gradient(#ef4444,#f59e0b,#eab308,#22c55e,#3b82f6,#8b5cf6,#ef4444)';

/** Largeur de la feuille A4 à l'écran (celle de DocumentA4), avant réduction. */
const LARGEUR_FEUILLE = 680;

/**
 * La feuille entière, réduite pour tenir dans la place donnée : rien n'est coupé.
 *
 * @param {object} props propriétés
 * @param {import('react').ReactNode} props.children la feuille
 * @returns {import('react').ReactElement} la feuille réduite, centrée
 */
function FeuilleEntiere({ children }) {
  const boite = useRef(null);
  const feuille = useRef(null);
  const [echelle, setEchelle] = useState(0);
  const [hauteur, setHauteur] = useState(0);
  useLayoutEffect(() => {
    const calcule = () => {
      const b = boite.current;
      const f = feuille.current;
      if (!b || !f) return;
      const h = f.scrollHeight;
      if (!h || !b.clientWidth || !b.clientHeight) return;
      setHauteur(h);
      setEchelle(Math.min(1, b.clientWidth / LARGEUR_FEUILLE, b.clientHeight / h));
    };
    calcule();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(calcule);
    ro.observe(boite.current);
    ro.observe(feuille.current);
    return () => ro.disconnect();
  }, []);
  return (
    <div
      ref={boite}
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: LARGEUR_FEUILLE * echelle,
          height: hauteur * echelle,
          flexShrink: 0,
          visibility: echelle ? 'visible' : 'hidden',
        }}
      >
        <div
          ref={feuille}
          style={{
            width: LARGEUR_FEUILLE,
            transform: 'scale(' + echelle + ')',
            transformOrigin: 'top left',
            boxShadow: '0 2px 10px rgba(15,23,42,.18)',
            pointerEvents: 'none',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Le modèle et la couleur des devis et factures, choisis en voyant l'offre
 * entière : chaque toucher change aussitôt la feuille. Rien n'est enregistré
 * avant « Enregistrer » ; « Annuler » garde les réglages d'avant.
 *
 * @param {object} props propriétés
 * @param {object} props.soc la société
 * @param {boolean} props.estMag offre d'exemple du magasin
 * @param {(changes: object) => void} props.set enregistre les réglages de la société
 * @param {() => void} props.onClose referme l'écran
 * @returns {import('react').ReactElement} l'écran plein
 */
export function StyleDesDocuments({ soc, estMag, set, onClose }) {
  const [style, setStyle] = useState(soc?.devisStyle || 'suisse');
  const [couleur, setCouleur] = useState(soc?.docCouleur || null);
  const { client, doc } = offreDExemple(soc, estMag, style, couleur);
  const accent = doc.accent.toLowerCase();
  const autre =
    couleur && couleur[0] === '#' && !COULEURS_DOCUMENT.some((c) => c.toLowerCase() === accent);
  const rond = (choisi) => ({
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: '1px solid rgba(15,23,42,.12)',
    boxShadow: choisi ? '0 0 0 3px #fff, 0 0 0 5px ' + C.navy : 'none',
    cursor: 'pointer',
    padding: 0,
    position: 'relative',
    overflow: 'hidden',
  });
  return (
    <PleinEcran>
      <div
        role="dialog"
        aria-label={tr('Modèle des documents')}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9000,
          background: '#e5e7eb',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            background: C.navy,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: 'calc(10px + env(safe-area-inset-top)) 14px 10px',
          }}
        >
          <button
            type="button"
            aria-label={tr('Annuler')}
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: 20,
              cursor: 'pointer',
              padding: 4,
            }}
          >
            ✕
          </button>
          <div style={{ flex: 1, textAlign: 'center', fontSize: 15, fontWeight: 800 }}>
            {tr('Modèle des documents')}
          </div>
          <div style={{ width: 28 }} />
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', padding: 10 }}>
          <FeuilleEntiere>
            <DocumentA4 preview soc={soc} client={client} doc={doc} onClose={() => {}} />
          </FeuilleEntiere>
        </div>
        <div
          style={{
            background: C.white,
            borderRadius: '16px 16px 0 0',
            padding: '12px 14px calc(12px + env(safe-area-inset-bottom))',
            boxShadow: '0 -6px 18px rgba(15,23,42,.12)',
            width: '100%',
            maxWidth: 560,
            margin: '0 auto',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 800, color: C.muted, marginBottom: 6 }}>
            {tr("Touche un modèle — l'aperçu se met à jour :")}
          </div>
          <div style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 6 }}>
            {DEVIS_STYLES.map((s) => {
              const on = style === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setStyle(s.id)}
                  style={{
                    flexShrink: 0,
                    cursor: 'pointer',
                    border: '2px solid ' + (on ? DA : C.border),
                    borderRadius: 10,
                    padding: '7px 12px',
                    background: on ? DA : C.white,
                    color: on ? '#fff' : C.ink,
                    fontSize: 11,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {on ? '✓ ' : ''}
                  {s.nom}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 10, fontWeight: 800, color: C.muted, margin: '8px 0 8px' }}>
            {tr('Couleur des documents')}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(9, 1fr)',
              gap: 6,
              justifyItems: 'center',
            }}
          >
            {soc?.logo && (
              <button
                type="button"
                aria-label={tr('Auto (logo)')}
                title={tr('Auto (logo)')}
                onClick={() =>
                  autoLogoColor(soc.logo).then((hex) => {
                    if (hex) setCouleur(hex);
                    else alert(tr('Logo trop clair ou gris : choisis une couleur manuellement.'));
                  })
                }
                style={{ ...rond(false), background: '#fff' }}
              >
                <img
                  src={soc.logo}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }}
                />
              </button>
            )}
            {COULEURS_DOCUMENT.map((hex) => (
              <button
                key={hex}
                type="button"
                aria-label={hex}
                aria-pressed={accent === hex.toLowerCase()}
                onClick={() => setCouleur(hex)}
                style={{ ...rond(accent === hex.toLowerCase()), background: hex }}
              />
            ))}
            <label
              title={tr('Autre')}
              style={{
                ...rond(autre),
                background: autre ? couleur : ARC_EN_CIEL,
                display: 'block',
              }}
            >
              <input
                type="color"
                aria-label={tr('Autre')}
                value={couleur && couleur[0] === '#' ? couleur : '#3366cc'}
                onChange={(e) => setCouleur(e.target.value)}
                style={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer',
                }}
              />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px 0',
                borderRadius: 12,
                border: '1.5px solid ' + C.border,
                background: C.white,
                color: C.ink,
                fontSize: 14,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              {tr('Annuler')}
            </button>
            <button
              type="button"
              onClick={() => {
                set({ devisStyle: style, docCouleur: couleur });
                onClose();
              }}
              style={{
                flex: 2,
                padding: '12px 0',
                borderRadius: 12,
                border: 'none',
                background: C.green,
                color: '#fff',
                fontSize: 14,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              {tr('✓ Enregistrer')}
            </button>
          </div>
        </div>
      </div>
    </PleinEcran>
  );
}
